import DynamicDialog from '@components/DynamicDialog';
import LoadingSpinner from '@components/LoadingSpinner';
import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import useDisclosure from '@/hooks/useDisclosure';
import { cn } from '@helpers/cn';
import { useTranslation } from 'react-i18next';
import { omit } from 'lodash-es';

type AspectRatio = `${number}/${number}`;

interface ImageWithPreviewProps {
  activatorProps?: {
    className?: string;
    [key: string]: unknown;
  };
  alt: string;
  fallbackImgSrc?: string;
  fitParent?: boolean;
  fitParentOptions?: {
    mode?: CSSProperties['objectFit'];
    position?: CSSProperties['objectPosition'];
  };
  imgSrc?: string;
  previewOptions?: {
    aspectRatio?: AspectRatio;
    width?: number;
  };
  small?: boolean;
  useNaturalImageSize?: boolean;
}

interface Image {
  image: HTMLImageElement | null;
  isError: boolean;
  isLoaded: boolean;
}

interface FullSizeImageProps {
  alt: ImageWithPreviewProps['alt'];
  aspectRatio?: AspectRatio;
  image: Image;
}

interface PreviewWrapperProps {
  children: ReactNode;
  className?: string;
  fitParent: ImageWithPreviewProps['fitParent'];
  previewImg: Image;
  previewOptions?: ImageWithPreviewProps['previewOptions'];
}

interface PreviewDefaults {
  aspectRatio: AspectRatio;
  height: number;
  objectFit: CSSProperties['objectFit'];
  objectPosition: CSSProperties['objectPosition'];
  width: number;
}

const PREVIEW_DEFAULTS = {
  aspectRatio: '16/9',
  height: 164,
  objectFit: 'contain',
  objectPosition: 'center',
  width: 290,
} as const satisfies PreviewDefaults;

const PlaceholderImage = () => {
  const { t } = useTranslation();

  return (
    <img
      alt={t('placeholder')}
      src='https://assets.eye-able.com/dashboard-assets/icons/placeholder-image.png'
      className='eye:h-[70px]'
    />
  );
};

const FullSizeImage = ({ alt, aspectRatio, image }: FullSizeImageProps) => {
  const { isError, isLoaded, image: img } = image ?? {};

  if (isError) return <PlaceholderImage />;

  if (!isLoaded) {
    return <LoadingSpinner height={PREVIEW_DEFAULTS.height} />;
  }

  return (
    <img
      alt={alt}
      src={img?.src}
      className={cn(
        'eye:z-1',
        aspectRatio &&
          'eye:relative eye:w-full eye:outline-4 eye:outline-black',
      )}
      style={aspectRatio ? { aspectRatio } : undefined}
    />
  );
};

const PreviewWrapper = ({
  children,
  fitParent,
  previewOptions,
  previewImg,
  className,
}: PreviewWrapperProps) => {
  const previewWidth = previewOptions?.width || PREVIEW_DEFAULTS.width;
  const previewWrapperWidth = usePreviewWrapperWidth(
    previewImg,
    previewWidth,
    fitParent,
  );

  return (
    <div
      aria-hidden
      style={{
        overflow: 'hidden',
        cursor: previewImg.isError ? 'default' : 'pointer',
        width: previewWrapperWidth,
        aspectRatio:
          previewOptions?.aspectRatio ?? PREVIEW_DEFAULTS.aspectRatio,
      }}
      className={className}
    >
      {children}
    </div>
  );
};

function ImageWithPreview({
  activatorProps = {},
  alt,
  fallbackImgSrc,
  fitParent = false,
  fitParentOptions,
  imgSrc,
  previewOptions,
  small = false,
  useNaturalImageSize = false,
}: ImageWithPreviewProps) {
  const { isOpen: isDialogOpen, toggle: toggleDialog } = useDisclosure(false);
  const previewImgSrc = getPreviewImgSrc(imgSrc, previewOptions);
  const previewImg = useLoadImage(previewImgSrc, fallbackImgSrc);
  const fullSizeImg = useLoadImage(imgSrc, fallbackImgSrc, isDialogOpen);

  if (previewImg.isError) {
    return (
      <PreviewWrapper
        previewImg={previewImg}
        previewOptions={previewOptions}
        fitParent={fitParent}
        className='eye:flex eye:justify-center eye:items-center'
      >
        <PlaceholderImage />
      </PreviewWrapper>
    );
  }

  if (!previewImg.image) {
    return <LoadingSpinner height={PREVIEW_DEFAULTS.height} />;
  }

  return (
    <DynamicDialog
      activatorProps={{
        'aria-label': alt,
        className: cn(
          'eye:overflow-hidden eye:w-full eye:border! eye:border-grey-20!',
          !fitParent && `eye:min-h-[${PREVIEW_DEFAULTS.height}px]`,
          activatorProps?.className,
        ),
        // TODO: eye:p-0 does not work 🫤
        style: { padding: 0 },
        ...omit(activatorProps, 'className'),
      }}
      activatorText={
        <PreviewWrapper
          previewImg={previewImg}
          previewOptions={previewOptions}
          fitParent={fitParent}
        >
          <img
            alt={alt}
            loading='eager'
            src={previewImg.image.src}
            className={cn(
              'eye:absolute eye:top-0 eye:left-0 eye:w-full eye:h-auto',
              fitParent && 'eye:h-full',
            )}
            style={{
              ...(fitParent && {
                objectFit: fitParentOptions?.mode || PREVIEW_DEFAULTS.objectFit,
                objectPosition:
                  fitParentOptions?.position || PREVIEW_DEFAULTS.objectPosition,
              }),
            }}
          />
        </PreviewWrapper>
      }
      fullWidth
      alwaysFullscreen={!small}
      aria-hidden
      hideActions
      onOpen={() => {
        toggleDialog();
        return true;
      }}
      onCancel={() => {
        toggleDialog();
        return true;
      }}
      title={alt}
    >
      {useNaturalImageSize ? (
        <div className='eye:text-center'>
          <FullSizeImage image={fullSizeImg} alt={alt} />
        </div>
      ) : (
        <FullSizeImage
          image={fullSizeImg}
          alt={alt}
          {...(fullSizeImg.image && {
            aspectRatio: `${fullSizeImg.image.naturalWidth}/${fullSizeImg.image.naturalHeight}`,
          })}
        />
      )}
    </DynamicDialog>
  );
}

function useLoadImage(
  imgSrc?: string,
  fallbackImgSrc?: string,
  shouldLoad = true,
) {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!shouldLoad || !imgSrc) return;

    let isCancelled = false;
    let hasLoaded = false;
    const image = new window.Image();

    function loadFallback() {
      if (fallbackImgSrc && imgSrc !== fallbackImgSrc) {
        const fallbackImage = new window.Image();

        fallbackImage.onload = () => {
          if (isCancelled) return;

          setLoadError(false);
          setImage(fallbackImage);
        };
        fallbackImage.onerror = () => {
          if (isCancelled) return;

          setLoadError(true);
          setImage(null);
        };
        fallbackImage.src = fallbackImgSrc;
      } else {
        if (isCancelled) return;

        setLoadError(true);
        setImage(null);
      }
    }

    const timeoutId = setTimeout(() => {
      if (hasLoaded || isCancelled) return;
      // Try loading fallback image after timeout
      loadFallback();
    }, 5000); // fallback after 5 seconds

    image.onload = () => {
      if (isCancelled || hasLoaded) return;

      hasLoaded = true;
      clearTimeout(timeoutId);
      setLoadError(false);
      setImage(image);
    };

    image.onerror = () => {
      if (isCancelled || hasLoaded) return;

      hasLoaded = true;
      clearTimeout(timeoutId);
      // Try loading fallback image on error
      loadFallback();
    };

    image.src = imgSrc;

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [imgSrc, shouldLoad, fallbackImgSrc]);

  return { image, isLoaded: !!image, isError: loadError };
}

function usePreviewWrapperWidth(
  previewImg: Image,
  width: number,
  fitParent?: boolean,
) {
  const naturalSize = useMemo(() => {
    if (!previewImg.image) return { width: 0, height: 0 };

    return {
      width: previewImg.image.naturalWidth,
      height: previewImg.image.naturalHeight,
    };
  }, [previewImg.image]);

  const wrapperWidth = useMemo(() => {
    if (fitParent) return '100%';

    return Math.min(naturalSize.width, width);
  }, [fitParent, width, naturalSize.width]);

  return wrapperWidth;
}

function getPreviewImgSrc(
  imgSrc: ImageWithPreviewProps['imgSrc'],
  previewOptions: ImageWithPreviewProps['previewOptions'],
) {
  if (!imgSrc) return;

  const aspectRatio =
    previewOptions?.aspectRatio || PREVIEW_DEFAULTS.aspectRatio;

  // https://docs.bunny.net/docs/stream-image-processing
  return `${imgSrc}?width=600&aspect_ratio=${aspectRatio.replace('/', ':')}&crop_gravity=north`;
}

export default ImageWithPreview;
